import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button } from '../../../components/ui/button';
import { whatsappService, WahaSessionStatus } from '../services/whatsappService';
import { Loader2, XCircle, RefreshCw } from 'lucide-react';
import { toast } from '../../../hooks/use-toast';

interface ReconnectModalProps {
  open: boolean;
  onClose: () => void;
  instanceName: string;
  connectionId: string;
  currentPhone?: string;
  onReconnected: (id: string, status: WahaSessionStatus, phone: string) => void;
}

type ReconnectStatus = 'restarting' | 'waiting' | 'error' | 'qr_expired';

const POLL_INTERVAL = 4000;
const MAX_STARTING_POLLS = 15; // ~60s en STARTING antes de auto-reiniciar
const MAX_RETRIES = 3;

/**
 * Modal liviano para RECONECTAR un canal de WhatsApp existente y generar el QR
 * sin salir de la app (igual que el dashboard de WAHA). A diferencia de
 * QRDisplayModal, NO ejecuta onboarding: no crea agente, no incrementa el
 * contador y al cancelar NO borra la conexión.
 */
export const ReconnectModal: React.FC<ReconnectModalProps> = ({
  open,
  onClose,
  instanceName,
  connectionId,
  currentPhone,
  onReconnected,
}) => {
  const [status, setStatus] = useState<ReconnectStatus>('restarting');
  const [qrCode, setQrCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  const cancelledRef = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startingCountRef = useRef(0);

  const cleanup = useCallback(() => {
    cancelledRef.current = true;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Éxito: marca la conexión como conectada en BD (sin onboarding ni contador)
  const handleReconnectSuccess = useCallback(async (phone: string) => {
    cleanup();
    try {
      const additionalData: { connected_at: string; numero_telefono?: string } = {
        connected_at: new Date().toISOString(),
      };
      // Solo escribir el número si tenemos uno real y distinto (no pisar uno bueno con '')
      if (phone && phone !== currentPhone) {
        additionalData.numero_telefono = phone;
      }
      await whatsappService.updateConnectionStatus(connectionId, 'connected', additionalData);
      toast.success('WhatsApp reconectado correctamente');
      onReconnected(connectionId, 'WORKING', phone || currentPhone || '');
      onClose();
    } catch (err: unknown) {
      const isDuplicate =
        err && typeof err === 'object' && 'code' in err && (err as { code: string }).code === '23505';
      if (isDuplicate) {
        setStatus('error');
        setErrorMessage(`El número +${phone} ya está conectado en otra instancia.`);
        toast.error('Ese número ya está conectado en otra instancia.');
      } else {
        console.error('[ReconnectModal] Error guardando reconexión:', err);
        setStatus('error');
        setErrorMessage('Error al guardar la reconexión. Intenta de nuevo.');
      }
      // IMPORTANTE: no borrar la conexión.
    }
  }, [cleanup, connectionId, currentPhone, onReconnected, onClose]);

  // Loop de polling (mismo patrón que QRDisplayModal, sin onboarding)
  const poll = useCallback(async () => {
    if (cancelledRef.current) return;
    try {
      const result = await whatsappService.pollSessionStatus(instanceName);
      if (cancelledRef.current) return;

      switch (result.status) {
        case 'WORKING':
          await handleReconnectSuccess(result.phoneNumber || '');
          return;
        case 'SCAN_QR_CODE':
          if (result.qrBase64) setQrCode(result.qrBase64);
          break;
        case 'STARTING':
          startingCountRef.current += 1;
          if (startingCountRef.current >= MAX_STARTING_POLLS) {
            startingCountRef.current = 0;
            try {
              await whatsappService.restartSession(instanceName);
            } catch (e) {
              console.error('[ReconnectModal] Error reiniciando sesión atascada:', e);
            }
          }
          break;
        case 'FAILED':
          cleanup();
          setStatus('qr_expired');
          setErrorMessage('El código QR expiró o la sesión falló.');
          return;
        case 'STOPPED':
          cleanup();
          setStatus('error');
          setErrorMessage('La sesión se detuvo inesperadamente.');
          return;
      }

      if (!cancelledRef.current) {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL);
      }
    } catch (error) {
      console.error('[ReconnectModal] Error en polling:', error);
      if (!cancelledRef.current) {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL);
      }
    }
  }, [instanceName, cleanup, handleReconnectSuccess]);

  // Arranque: reiniciar la sesión y obtener el QR
  const initReconnect = useCallback(async () => {
    cancelledRef.current = false;
    startingCountRef.current = 0;
    setStatus('restarting');
    setErrorMessage('');
    setQrCode('');

    try {
      // Sondear el estado actual de la sesión
      let info: Awaited<ReturnType<typeof whatsappService.getSessionInfo>> | null = null;
      let sessionMissing = false;
      try {
        info = await whatsappService.getSessionInfo(instanceName);
      } catch {
        sessionMissing = true; // 404 / la sesión no existe
      }
      if (cancelledRef.current) return;

      if (sessionMissing) {
        // La sesión ya no existe en WAHA → recrearla con el mismo nombre.
        // (Único camino que recrea; el resto usa restart para preservar webhooks.)
        await whatsappService.createSession(instanceName);
        await whatsappService.startSession(instanceName);
      } else if (info && info.status === 'WORKING') {
        // Ya está enlazada (p.ej. la rescanearon en el dashboard) → éxito directo, sin QR.
        const phone = info.me?.id ? info.me.id.replace('@c.us', '') : '';
        await handleReconnectSuccess(phone);
        return;
      } else {
        // STOPPED / STARTING / SCAN_QR_CODE / FAILED → restart (preserva config/webhooks).
        await whatsappService.restartSession(instanceName);
      }

      // Dar un momento a WAHA para arrancar
      await new Promise((resolve) => setTimeout(resolve, 3000));
      if (cancelledRef.current) return;

      const result = await whatsappService.pollSessionStatus(instanceName);
      if (cancelledRef.current) return;

      if (result.status === 'WORKING') {
        await handleReconnectSuccess(result.phoneNumber || '');
        return;
      }
      if (result.qrBase64) setQrCode(result.qrBase64);
      setStatus('waiting');
      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL);
    } catch (err) {
      console.error('[ReconnectModal] Error iniciando reconexión:', err);
      if (cancelledRef.current) return;
      setStatus('error');
      setErrorMessage('No se pudo iniciar la reconexión. Intenta de nuevo.');
    }
  }, [instanceName, handleReconnectSuccess, poll]);

  // El modal se monta solo cuando hay un canal objetivo (open siempre true en vida útil)
  useEffect(() => {
    if (!open) return;
    void initReconnect();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Regenerar QR: 1er intento restart; desde el 2do escala a logout + start (QR nuevo)
  const handleRegenerateQR = async () => {
    if (retryCount >= MAX_RETRIES) {
      setStatus('error');
      setErrorMessage('Se alcanzó el límite de reintentos. Cierra e intenta de nuevo.');
      return;
    }
    const nextRetry = retryCount + 1;
    setRetryCount(nextRetry);
    setStatus('restarting');
    setErrorMessage('');
    setQrCode('');
    cancelledRef.current = false;
    startingCountRef.current = 0;

    try {
      if (nextRetry >= 2) {
        // Escalada: forzar re-vinculación (QR nuevo)
        await whatsappService.logoutSession(instanceName);
        await whatsappService.startSession(instanceName);
      } else {
        await whatsappService.restartSession(instanceName);
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
      if (cancelledRef.current) return;

      const result = await whatsappService.pollSessionStatus(instanceName);
      if (cancelledRef.current) return;

      if (result.status === 'WORKING') {
        await handleReconnectSuccess(result.phoneNumber || '');
        return;
      }
      if (result.qrBase64) setQrCode(result.qrBase64);
      setStatus('waiting');
      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL);
    } catch (error) {
      console.error('[ReconnectModal] Error regenerando QR:', error);
      setStatus('error');
      setErrorMessage('Error al regenerar el código QR. Intenta de nuevo.');
    }
  };

  const handleCancel = () => {
    // Cerrar sin borrar la conexión ni detener la sesión (sigue corriendo en background).
    cleanup();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleCancel} />
      <div className="relative bg-white rounded-2xl shadow-lg w-[90%] max-w-md p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-semibold text-lg text-maxi-text-dark">Reconectar WhatsApp</h2>
          <Button variant="ghost" className="text-maxi-text-dark text-2xl" onClick={handleCancel}>
            ×
          </Button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Instancia: <span className="font-mono">{instanceName}</span>
        </p>

        <div className="space-y-4">
          {/* --- Reiniciando --- */}
          {status === 'restarting' && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-blue-500 mb-3" />
              <p className="text-sm text-gray-600">Reiniciando la sesión...</p>
            </div>
          )}

          {/* --- Esperando escaneo del QR --- */}
          {status === 'waiting' && (
            <>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  <span className="text-sm text-gray-600">Esperando escaneo del código QR...</span>
                </div>
              </div>
              {qrCode ? (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-center">
                    <img src={qrCode} alt="Código QR de WhatsApp" className="w-64 h-64 object-contain" />
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-500 mb-2" />
                  <p className="text-xs text-gray-500">Generando código QR...</p>
                </div>
              )}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-sm text-blue-900 mb-2">Instrucciones:</h3>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Abre WhatsApp en tu teléfono</li>
                  <li>Ve a Configuración &gt; Dispositivos vinculados</li>
                  <li>Toca "Vincular un dispositivo"</li>
                  <li>Escanea este código QR</li>
                </ol>
              </div>
            </>
          )}

          {/* --- Error --- */}
          {status === 'error' && (
            <div className="text-center py-4">
              <XCircle className="w-16 h-16 mx-auto text-red-500 mb-2" />
              <p className="text-lg font-semibold text-red-600">Error</p>
              <p className="text-sm text-gray-600 mt-2">{errorMessage}</p>
              {retryCount < MAX_RETRIES && (
                <Button variant="outline" onClick={handleRegenerateQR} className="mt-4">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reintentar
                </Button>
              )}
            </div>
          )}

          {/* --- QR expirado --- */}
          {status === 'qr_expired' && (
            <div className="text-center py-4">
              <RefreshCw className="w-16 h-16 mx-auto text-orange-500 mb-2" />
              <p className="text-lg font-semibold text-orange-600">Código QR expirado</p>
              <p className="text-sm text-gray-600 mt-2">{errorMessage}</p>
              {retryCount < MAX_RETRIES ? (
                <Button variant="outline" onClick={handleRegenerateQR} className="mt-4">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerar QR ({MAX_RETRIES - retryCount} intentos restantes)
                </Button>
              ) : (
                <p className="text-xs text-red-500 mt-2">
                  Se alcanzó el límite de reintentos. Cierra e intenta de nuevo.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={handleCancel}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReconnectModal;
